/**
 * Unified Support/Explanation Content Types
 *
 * This file is the SINGLE SOURCE OF TRUTH for all support content types.
 *
 * Hierarchy:
 * - `explanation`: Student-facing "Zo werkt het" — shown after incorrect answer or on-demand
 * - `afterSummary`: Session-end summary text — shown in SessionSummary.tsx
 * - `cheatSheet`: Quick-reference content — shown in CheatSheetDrawer.tsx on-demand
 * - `hints`: Progressive hints — shown during question attempt
 *
 * NOTE: `steps` is DEPRECATED (v4.3). The howItWorks/explanation body text with newlines
 * is automatically rendered as numbered steps by ExplanationSteps. The separate steps[]
 * array is redundant and will be removed in a future version.
 *
 * Resolution priority: seed-level > family-level (seed is more specific)
 */

export interface ExplanationContent {
  /** Display title, e.g. "Zo werkt het" */
  title: string
  /** Main explanation body text */
  body: string
  /**
   * Step-by-step breakdown
   * @deprecated v4.3 — Use newline-separated text in `body` instead.
   * ExplanationSteps automatically splits body text on newlines into numbered steps.
   * This field will be removed in a future version.
   */
  steps?: string[]
  /** Progressive hints (shown one at a time) */
  hints?: string[]
  /** Reference to a visual aid type (optional) */
  visualRef?: string
  /** Where this content originated */
  source: 'family' | 'seed' | 'override'
}

export interface CheatSheetContent {
  /** Cheat sheet title */
  title: string
  /** Main content */
  body: string
  /** Key rules or formulas */
  rules?: string[]
  /** Example problems */
  examples?: string[]
  /** Where this content originated */
  source: 'family' | 'seed' | 'override'
}

export interface ResolvedSupport {
  /** Primary explanation ("Zo werkt het") */
  explanation: ExplanationContent | null
  /** Cheat sheet content for drawer */
  cheatSheet: CheatSheetContent | null
  /** Session-end summary text */
  afterSummary: string | null
  /** Progressive hints */
  hints: string[]
  /**
   * Step-by-step walkthrough
   * @deprecated v4.3 — Use explanation.body with newlines instead.
   * Will be removed in a future version.
   */
  steps: string[]
  /** Coverage report for diagnostics */
  coverage: SupportCoverage
}

export interface SupportCoverage {
  hasExplanation: boolean
  hasCheatSheet: boolean
  hasHints: boolean
  /**
   * @deprecated v4.3 — Steps are derived from howItWorks newlines.
   * Kept for backward compatibility. Will be removed in a future version.
   */
  hasSteps: boolean
  hasAfterSummary: boolean
  /** Percentage of fields populated (0-100) */
  completeness: number
  /** Sources used in resolution */
  sources: ('family' | 'seed' | 'override')[]
}

/**
 * Family-level support definition.
 * Used in familySupportRegistry.ts.
 */
export interface FamilySupportDefinition {
  /** Family ID (e.g. "GET-KOMMA-AFRONDEN") */
  familyId: string
  /** "Zo werkt het" explanation */
  howItWorks?: string
  /**
   * Step-by-step walkthrough
   * @deprecated v4.3 — Use newline-separated text in howItWorks instead.
   * Will be removed in a future version.
   */
  steps?: string[]
  /** Progressive hints */
  hints?: string[]
  /** Cheat sheet content */
  cheatSheet?: {
    title?: string
    body?: string
    rules?: string[]
    examples?: string[]
  }
  /** Session-end summary */
  afterSummary?: string
}

/**
 * Seed-level support definition.
 * Matches the `support` field in seed JSON files.
 */
export interface SeedSupportDefinition {
  howItWorks?: string
  afterSummary?: string
  /**
   * @deprecated v4.3 — Use newline-separated text in howItWorks instead.
   * Will be removed in a future version.
   */
  steps?: string[]
  hints?: string[]
}

/**
 * Calculate support coverage completeness percentage.
 */
export function calculateCoverage(
  coverage: Omit<SupportCoverage, 'completeness' | 'sources'>,
): number {
  const fields = [
    coverage.hasExplanation,
    coverage.hasCheatSheet,
    coverage.hasHints,
    coverage.hasSteps,
    coverage.hasAfterSummary,
  ]
  const filled = fields.filter(Boolean).length
  return Math.round((filled / fields.length) * 100)
}
